import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { TripStateKey } from '@apps/main/modules/state/state';
import { useItineraryStore } from '@apps/main/modules/store/itineraryStore';
import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';
import type {
  PriceCalendarDaysSelectDateEvent,
  PriceCalendarMonth,
} from '@apps/main/new-desktop/modules/priceCalendar';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';

import { weekdays } from '@libs/sembo-dates';
import type { AppSelectOption } from '@libs/ui';
import {
  trackAvailableDateClicked,
  trackNoAvailableDateClicked,
} from '@libs/google-analytics-events';

import { useBrowserStore } from '../browserStore';
import { useStatsigEventsStore } from '../statsigEvents/statsigEventsStore';
import type { CoreEventSource } from '../../models';

import {
  getPriceCalendarMonths,
  getSelectedMonthOption,
  getMonthsSelectOptions,
} from './priceCalendarStoreLogic';

export const usePriceCalendarStore = defineStore('PriceCalendarStore', () => {
  const tripState = injectStrict(TripStateKey);
  const browserStore = useBrowserStore();
  const itineraryStore = useItineraryStore();
  const preferredTimeStore = usePreferredTimeStore();
  const statsigEventsStore = useStatsigEventsStore();

  // State
  const showPulsing = ref<boolean>(false);

  // Actions

  function selectItineraryDate(
    eventProps: PriceCalendarDaysSelectDateEvent,
    source: CoreEventSource
  ): void {
    statsigEventsStore.logCoreDateChanged({ source });
    itineraryStore.toggleItinerary(eventProps.date);

    if (eventProps.hasPrice) {
      trackAvailableDateClicked();
    } else {
      trackNoAvailableDateClicked();
    }
  }

  function selectItineraryMonth(date: Date, source: CoreEventSource): void {
    statsigEventsStore.logCoreDateChanged({ source });
    tripState.toggleExpandedMonth(date);
  }

  // Getters
  const allowHoverActions = computed(() => !browserStore.isSmallDevice);

  const monthsSelectOptions = computed<AppSelectOption<string>[]>(() =>
    getMonthsSelectOptions(tripState.months.value)
  );

  const priceCalendarMonths = computed<PriceCalendarMonth[]>(() =>
    getPriceCalendarMonths(
      tripState.months.value,
      itineraryStore.itineraryDates,
      itineraryStore.isSearchingItineraries,
      weekdays(),
      itineraryStore.isSelectedItinerary,
      preferredTimeStore.isDateInExpandedMonth,
      itineraryStore.selectedItinerary
    )
  );

  const selectedMonthOption = computed<AppSelectOption<string>>(() =>
    getSelectedMonthOption(
      monthsSelectOptions.value,
      preferredTimeStore.isDateInExpandedMonth
    )
  );

  const getCurrentMonth = computed<PriceCalendarMonth | undefined>(() =>
    priceCalendarMonths.value.find(
      (month: PriceCalendarMonth) => month.days.length > 0
    )
  );

  // State
  function showPulsingIndicator(): void {
    showPulsing.value = true;
  }

  function hidePulsingIndicator(): void {
    showPulsing.value = false;
  }

  return {
    // Actions
    selectItineraryDate,
    selectItineraryMonth,
    showPulsingIndicator,
    hidePulsingIndicator,

    // Getters
    allowHoverActions,
    monthsSelectOptions,
    priceCalendarMonths,
    selectedMonthOption,
    getCurrentMonth,

    // State
    showPulsing,
  };
});
